import { Component, OnInit, OnDestroy, AfterViewInit, Input, ViewChild, ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import { UrlLoaderService } from '../url-loader.service';
import { AudioBufferService, VoiceSound } from '../audio-buffer.service';
import { Subject, Subscription } from 'rxjs';
import { memo } from '../element-render-video/element-render-video.component';
import { InteractionTrackingService, MediaEvent } from '../interaction-tracking.service';
import { STUDENT_ACTIVITY_SLUGS } from '../test-runner/test-runner.component';

export interface IAudioSrcInfo {
  containing_item_id?: number;
  url: string; 
  isSimpleDisplay?: boolean;
  isAutoPlay?: boolean;
  audioCurrentTime?: number;
  audioDuration?: number;
}

@Component({
  selector: 'render-audio',
  templateUrl: './render-audio.component.html',
  styleUrls: ['./render-audio.component.scss']
})
export class RenderAudioComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {

  @Input() url: string;
  @Input() hasControls: boolean = false;
  @Input() trigger: Subject<boolean>;
  @Input() accessibleLabel:string;
  @Input() isTriggerDisabled: boolean;
  @ViewChild('audioPlayer') audioPlayer: ElementRef<HTMLAudioElement>;
  @Input() containingItemId?:number;

  sound: VoiceSound;
  audioSources = new Map();
  private triggerSub: Subscription;
  
  constructor(
    private urlLoader: UrlLoaderService,
    private audioBuffer: AudioBufferService,
    private interactionTracking: InteractionTrackingService
  ) { }

  ngOnInit(): void { }

  ngAfterViewInit() {
    const audioEl = this.audioPlayer?.nativeElement;

    if (!this.hasControls) {
      if (this.trigger) {
        this.triggerSub = this.trigger.subscribe(this.onTriggerPlay);
      }
      this.sound = new VoiceSound(audioEl, this.audioBuffer);
    }

    if (audioEl) {
      // 1. Wait for metadata to load before logging initial state
      if (audioEl.readyState >= 1) {
        this.initAudioLog();
      } else {
        audioEl.addEventListener('loadedmetadata', () => this.initAudioLog(), { once: true });
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    const urlChange = changes['url'];
    const itemChange = changes['containingItemId'];

    if (urlChange || itemChange) {
      
      // Prevent logging on the very first initialization component load
      const isUrlFirstChange = urlChange?.isFirstChange();
      const isItemFirstChange = itemChange?.isFirstChange();

      if (!isUrlFirstChange && !isItemFirstChange) {
        
        // Extract previous values safely
        const previousUrl = urlChange ? urlChange.previousValue : this.url;
        const previousItemId = itemChange ? itemChange.previousValue : this.containingItemId;

        // Only force the audio element to reload if the URL itself actually changed
        if (urlChange && this.audioPlayer) {
          const audioEl = this.audioPlayer.nativeElement;
          audioEl.load();
          
          if (audioEl.readyState >= 1) {
            this.logUrlChange(previousUrl, previousItemId);
          } else {
            audioEl.addEventListener('loadedmetadata', () => this.logUrlChange(previousUrl, previousItemId), { once: true });
          }
        }
      }
    }
  }

  ngOnDestroy() {
    if (this.triggerSub) {
      this.triggerSub.unsubscribe();
    }
    if (this.sound) {
      this.sound.removeFromBuffer();
    }
    this.destroyAudioLog();
  }

  onTriggerPlay = (isActive: boolean) => {
    if (isActive && !this.isTriggerDisabled) {
      this.sound.play();
    }
  }

  sanitizeUrl(url: string) {
    return this.urlLoader.sanitize(this.url);
  }

  getAccessibleLabel(){
    // setTimeout(()=>{
    //   console.log(this.audioSources);
    // }, 500)
    return this.accessibleLabel || 'Audio player';
  }

  getAudioSources() {
    return memo(this.audioSources, this.url, url => {
      const sanitized = this.urlLoader.sanitize(url);
      return [{ src: url, type: 'audio/mp3' }];
    });
  }

  pause = () => {
    if (this.audioPlayer?.nativeElement?.currentTime) {
      this.onPauseLog();
    }
  }

  donePlaying = (isFromComponent: boolean = false) => {
    if (isFromComponent) {
      this.onEndedLog();
    }
  }

  // Helper method to safely pull current metrics
  private getAudioSrcInfo(overrideTime?: number): IAudioSrcInfo {
    const audioEl = this.audioPlayer?.nativeElement;
    return {
      url: this.url,
      audioCurrentTime: overrideTime !== undefined ? overrideTime : (audioEl?.currentTime || 0),
      audioDuration: Number.isNaN(audioEl?.duration) ? 0 : (audioEl?.duration || 0),
      containing_item_id: this.containingItemId,
    };
  }

  private initAudioLog() {
    this.interactionTracking.logEvent(
      MediaEvent.INIT_COMPONENT, 
      this.getAudioSrcInfo(), 
      STUDENT_ACTIVITY_SLUGS.STUDENT_ASMT_TOOL_TTS
    );
  }
  
  private destroyAudioLog() {
    this.interactionTracking.logEvent(
      MediaEvent.DESTROY_COMPONENT, 
      this.getAudioSrcInfo(), 
      STUDENT_ACTIVITY_SLUGS.STUDENT_ASMT_TOOL_TTS
    );
  }

  private logUrlChange(previousUrl: string, previousItemId?: number) {
    const payload = {
      ...this.getAudioSrcInfo(), // Already has the NEW url and NEW containingItemId
      previous_url: previousUrl,
      previous_containing_item_id: previousItemId
    };

    this.interactionTracking.logEvent(
      'URL_CHANGED', 
      payload, 
      STUDENT_ACTIVITY_SLUGS.STUDENT_ASMT_TOOL_TTS
    );
  }

  onPlayLog = () => {
    this.interactionTracking.logEvent(
      MediaEvent.PLAY, 
      this.getAudioSrcInfo(), 
      STUDENT_ACTIVITY_SLUGS.STUDENT_ASMT_TOOL_TTS
    );
  };
  
  private onPauseLog = () => {
    const audio = this.audioPlayer?.nativeElement;
    if (!audio || audio.ended) return;
    
    this.interactionTracking.logEvent(
      MediaEvent.PAUSE, 
      this.getAudioSrcInfo(audio.currentTime), 
      STUDENT_ACTIVITY_SLUGS.STUDENT_ASMT_TOOL_TTS
    );
  };
    
  private onEndedLog = () => {
    const audio = this.audioPlayer?.nativeElement;
    // When ended, passing duration as currentTime ensures full play duration is recorded
    const finalTime = audio?.duration || audio?.currentTime || 0;
    
    this.interactionTracking.logEvent(
      MediaEvent.ENDED, 
      this.getAudioSrcInfo(finalTime), 
      STUDENT_ACTIVITY_SLUGS.STUDENT_ASMT_TOOL_TTS
    );
  };
}